'use client'

// imports
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import { useAtom, useSetAtom } from 'jotai';
import Link from 'next/link';

// lib files
import { DONATE_FALLBACK_URL, PASSPORT_LOOKUP_URL } from '@/lib/constants';
import { formatAmount } from '@/lib/helpers/format-amount';
import { getPassportLearnMoreUrl } from '@/lib/helpers/get-passport-learn-more-url';
import { VideoClass } from '@/lib/types/api/video';
import { sendPbsGtmEvent } from "@/lib/helpers/send-pbs-gtm-event";
import { stationDataAtom } from '@/lib/atoms/station-data';
import { StationData } from '@/lib/types/api/stations-data';
import { signinModalAtom, SignInModalStateEnum } from '@/lib/atoms/signin-modal';

// components
import DonateLinkButton from '@/components/Button/DonateLinkButton';
import LinkButton from '@/components/Button/LinkButton';

// svgs
import PassportCompass from '@/public/svg/compass-rose.svg';

// styles
import styles from './PassportBenefitScreen.module.scss';

interface PassportBenefitScreenProps {
  video: VideoClass;
  depStationData?: StationData;
  csStationDataPassportLearnMoreUrl?: string;
}

const PassportBenefitScreen = (props: PassportBenefitScreenProps) => {
  const { video, depStationData, csStationDataPassportLearnMoreUrl } = props;
  const setSigninModalOpen = useSetAtom(signinModalAtom);

  let [stationData] = useAtom(stationDataAtom);

  if (!stationData) {
    if (depStationData) {
      stationData = depStationData;
    } else {
      return null;
    }
  }

  const { attributes: {
    passport_url,
    short_common_name,
    annual_passport_qualifying_amount,
  } } = stationData!;

  const passportLearnMoreUrl = csStationDataPassportLearnMoreUrl || getPassportLearnMoreUrl(stationData);

  let explanationText = <>This episode is part of an extended library of videos available with {short_common_name} Passport.</>;

  if (annual_passport_qualifying_amount !== null) {
    const annualAmount = formatAmount(annual_passport_qualifying_amount);
    const monthlyAmount = formatAmount(annual_passport_qualifying_amount / 12);
    explanationText = <>This episode is part of an extended library of videos available with {short_common_name} Passport for a donation of at least ${monthlyAmount} per month or ${annualAmount} per year.</>;
  }

  const donateUrl = passport_url ? passport_url : DONATE_FALLBACK_URL;

  const baseEventData = {
    feature_category: 'passport benefit screen',
    show_title: video.show?.title,
    video_title: video.title,
    video_tp_media_id: video.legacy_tp_media_id,
    video_type: video.video_type,
  }

  const handleWhatIsPassportClick = () => {
    sendPbsGtmEvent({
      ...baseEventData,
      feature_name: "what is passport link",
      object_action: "click",
      object_action_behavior: "send user to learn more about passport",
      object_text: "What is Passport?",
      object_type: "link",
    });
  };

  const handleSignInClick = () => {
    setSigninModalOpen(SignInModalStateEnum.True);
    sendPbsGtmEvent({
      ...baseEventData,
      feature_name: "sign in button",
      object_action: "click",
      object_action_behavior: "open sign in modal",
      object_text: "Sign In",
      object_type: "button",
    });
  }

  return (
    <div className={styles.passport_benefit_screen} data-sentry-component="PassportBenefitScreen" data-sentry-source-file="PassportBenefitScreen.tsx">
      <h2 className={styles.passport_benefit_headline}>
        <span className={styles.passport_benefit_headline_intro}>Watch this video with</span>
        <span className={styles.passport_benefit_station_lockup}>
          {short_common_name}
          <PassportCompass className={styles.passport_benefit_screen_passport_compass} data-sentry-element="PassportCompass" data-sentry-source-file="PassportBenefitScreen.tsx" />
          Passport
        </span>
      </h2>

      <p className={styles.passport_benefit_explanation}>
        {explanationText}
      </p>

      {passportLearnMoreUrl && (
      <Link href={passportLearnMoreUrl} className={styles.passport_benefit_plm_link_small} onClick={handleWhatIsPassportClick}>
        What is Passport?
      </Link>
      )}

      <div className={styles.passport_benefit_screen_ctas}>
        <DonateLinkButton
          href={donateUrl}
          style='light_blue'
          size='min'
          gtmEventData={{
            ...baseEventData,
            feature_name: "passport benefit screen donate link",
            object_text: "Donate & Start Watching",
            object_url: donateUrl,
            object_type: "link",
          }}
        data-sentry-element="DonateLinkButton" data-sentry-source-file="PassportBenefitScreen.tsx">
          Donate & Start Watching
        </DonateLinkButton>

        {passportLearnMoreUrl && (
        <LinkButton href={passportLearnMoreUrl} size='min' className={styles.passport_benefit_plm_link_lg} onClick={handleWhatIsPassportClick}>
          What is Passport?
        </LinkButton>
        )}
      </div>

      <p className={styles.passport_benefit_screen_support}>
        Already a Member of {short_common_name}?
        <button
          className={styles.passport_benefit_screen_signin_button}
          onClick={handleSignInClick}
        >Sign In</button> or
        <Link
          href={PASSPORT_LOOKUP_URL}
          target="_blank"
        data-sentry-element="Link" data-sentry-source-file="PassportBenefitScreen.tsx">Check to see</Link> |
        <Link
          href="https://help.pbs.org/support/solutions/5000121793"
          target="_blank"
          data-sentry-element="Link" data-sentry-source-file="PassportBenefitScreen.tsx">
          Contact {short_common_name} Support
          </Link>
      </p>
    </div>

  );
};

export default PassportBenefitScreen;
